import { FC } from 'react'

import {
  Text,
  IconBank,
  IconBuildings,
  IconCalendarDays,
  IconSignature,
  IconReceiptBill,
  IconReceiptCredit,
  IconSignal,
  IconUserGroup,
  GreyGrey60,
  GreyGrey70,
  Primary13Regular
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'

import { ActivityEntityType, ActivityType } from '../types'
import { ActivityLogItemTemplate } from '../ActivityLogItemTemplate'
import {
  type ActivityLogDiffFn,
  defaultDiffFn,
  getDiffChanges,
  parseJavaValue
} from './activityLogDiff.helpers'
import { getRecipients } from './activityLogRecipients.helpers'

export const ActivityLogItem: FC<{
  name: string
  email: string | undefined
  createdAt: string
  activityType: ActivityType
  entityType: ActivityEntityType
  isLast: boolean
  metadata?: unknown
  'data-testid': string
  /** When provided, only show diff changes for these field names */
  filterFields?: Set<string>
  /** Override the activity log item title */
  itemTitle?: string
  /** Custom diff function for domain-specific formatting */
  diffFn?: ActivityLogDiffFn
}> = ({
  name,
  email,
  createdAt,
  activityType,
  entityType,
  isLast,
  metadata,
  'data-testid': dataTestId,
  filterFields,
  itemTitle,
  diffFn
}) => {
  const entityIcon = getActivityLogItemIcon(entityType)
  const allDiffChanges = getDiffChanges(metadata)
  const isBillingDocumentEmail =
    activityType === 'NOTIFICATION' &&
    (entityType === 'INVOICE' || entityType === 'CREDIT_NOTE')
  const recipients = isBillingDocumentEmail ? getRecipients(metadata) : []

  const filteredChanges = filterFields
    ? allDiffChanges.filter(change => filterFields.has(change.field))
    : allDiffChanges

  const resolveDiff = diffFn ?? defaultDiffFn

  const results = filteredChanges.map(change =>
    resolveDiff({
      field: change.field,
      previousValue: parseJavaValue(change.previousValue),
      newValue: parseJavaValue(change.newValue)
    })
  )

  const allLines = results.flatMap(r => r.lines)
  const titleOverride = results.find(r => r.title !== undefined)?.title

  const title = titleOverride ?? itemTitle ?? name

  return (
    <ActivityLogItemTemplate
      icon={entityIcon}
      isLast={isLast}
      data-testid={dataTestId}
    >
      <ActivityLogItemTemplate.Body>
        <ActivityLogItemTemplate.Title>{title}</ActivityLogItemTemplate.Title>
        {allLines.map((line, index) => (
          <Text
            key={index}
            {...Primary13Regular}
            color={GreyGrey70}
            data-testid={`activity-log.item.diff.${index}`}
          >
            {line}
          </Text>
        ))}
        {recipients.length > 0 && (
          <Text
            {...Primary13Regular}
            color={GreyGrey70}
            data-testid="activity-log.item.recipients"
          >
            {`Sent to ${recipients.join(', ')}`}
          </Text>
        )}
        <ActivityLogItemTemplate.Metadata email={email} createdAt={createdAt} />
      </ActivityLogItemTemplate.Body>
    </ActivityLogItemTemplate>
  )
}

const getActivityLogItemIcon = (entityType: ActivityEntityType) => {
  const Icon = match(entityType)
    .with('BILLING_SCHEDULE', () => IconCalendarDays)
    .with('CUSTOMER', () => IconBuildings)
    .with('SEQUENCE_ACCOUNT', () => IconBuildings)
    .with('INVOICE', () => IconReceiptBill)
    .with('SEQUENCE_USER', () => IconUserGroup)
    .with('WORKFLOW', () => IconSignal)
    .with('QUOTE', () => IconSignature)
    .with('MERCHANT', () => IconBank)
    .with('CREDIT_NOTE', () => IconReceiptCredit)
    .exhaustive()

  return <Icon color={GreyGrey60} />
}
